import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { BackBar } from "../components/BackBar";
import { CheckCircle2, Mail } from "lucide-react";

export default function ForgotPasswordPage() {
  const { lang } = useLang();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-12" data-testid="forgot-page">
      <BackBar to="/login" fallbackLabelFr="Connexion" fallbackLabelEn="Login" />
      <p className="overline mb-3">OFA France</p>
      <h1 className="font-serif text-4xl tracking-tighter mb-2">
        {lang === "fr" ? "Mot de passe oublié ?" : "Forgot your password?"}
      </h1>
      <p className="text-muted-foreground mb-8 text-sm">
        {lang === "fr"
          ? "Saisissez l'email associé à votre compte. Nous vous enverrons un lien pour choisir un nouveau mot de passe (valable 60 minutes)."
          : "Enter the email associated with your account. We'll send you a link to choose a new password (valid 60 minutes)."}
      </p>

      {done ? (
        <Card className="border-emerald-300/60 bg-emerald-50/40" data-testid="forgot-done">
          <CardContent className="p-6 space-y-3 text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
            <h2 className="font-display font-semibold">
              {lang === "fr" ? "Email envoyé" : "Email sent"}
            </h2>
            <p className="text-sm text-muted-foreground">
              {lang === "fr"
                ? "Si un compte existe pour cet email, vous recevrez un lien de réinitialisation dans quelques instants. Pensez à vérifier vos spams."
                : "If an account exists for this email, you'll receive a reset link shortly. Please check your spam folder too."}
            </p>
            <Link to="/login" className="inline-block text-sm text-accent font-medium" data-testid="back-to-login">
              {lang === "fr" ? "Retour à la connexion" : "Back to login"}
            </Link>
          </CardContent>
        </Card>
      ) : (
        <form onSubmit={onSubmit} className="space-y-5">
          <div>
            <Label className="text-sm">Email</Label>
            <Input
              data-testid="forgot-email"
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <Button
            data-testid="forgot-submit"
            type="submit"
            disabled={submitting || !email}
            className="w-full bg-primary text-primary-foreground"
          >
            <Mail className="w-4 h-4 mr-1.5" />
            {submitting
              ? (lang === "fr" ? "Envoi en cours…" : "Sending…")
              : (lang === "fr" ? "Envoyer le lien de réinitialisation" : "Send reset link")}
          </Button>
        </form>
      )}

      <div className="mt-6 text-sm text-muted-foreground">
        {lang === "fr" ? "Vous vous en souvenez ? " : "Remember it? "}
        <Link to="/login" className="text-accent font-medium" data-testid="goto-login">
          {lang === "fr" ? "Connectez-vous" : "Sign in"}
        </Link>
      </div>
    </div>
  );
}
